import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  Badge,
  Alert,
  InputGroup,
  ProgressBar
} from "react-bootstrap";
import Select from 'react-select';
import Useaxios from "utils/Useaxios";
import { API } from "utils/Apiroutes";
import CommonModal from "components/ui/CommonModal";
import ActionButtons from "components/ui/ActionButtons";
import AddButton from "components/ui/AddButton";
import { imageUrl } from "utils/helper";
import { GRADIENTS } from "utils/theme";
import usePagination from "hooks/usePagination";
import Pagination from "components/ui/Pagination";
import TableFilter from "components/ui/TableFilter";
import './Product.css';
import productService from "services/productService";
import { formatSlot } from "utils/helper";
import { useSearchParams } from "react-router-dom";
import { 
  getGlobalCurrency, 
  convertPrice, 
  setExchangeRate, 
  setGlobalCurrency 
} from "utils/currency";

function Product() {
  const { fetchData } = Useaxios();

  const [searchParams, setSearchParams] = useSearchParams();

  // Get a single value
  const category = searchParams.get('category');

  const { getProductDataWithDetails } = productService()

  const fetchProducts = useCallback(async (params) => {
    const res = await fetchData({
      url: API.getProduct,
      method: 'GET',
      params,
      showMessage: false
    });
    return res;
  }, []);

  const {
    data: products,
    loading,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    filters,
    goToPage,
    nextPage,
    prevPage,
    changePageSize,
    updateFilters,
    clearFilters,
    refresh
  } = usePagination(fetchProducts, { name: '', category: category ? category : '' });

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    basePrice: '',
    currencyType: 'LRD',
    centralStock: '',
    categoryId: null,
    machineIds: [],
    selectedSlots: [], // New field for slot selection
    isDiscounted: false,
    discountValue: '',
    productImage: null
  });
  const [availableMachines, setAvailableMachines] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]); // New state for slots
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [currentMachineSlots, setCurrentMachineSlots] = useState([]);
  const [selectedMachineForSlots, setSelectedMachineForSlots] = useState(null);
  const [stockQuantityError, setStockQuantityError] = useState(false);
  const [fetchedMachines, setFetchedMachines] = useState(new Set());
const [selectedCurrency, setSelectedCurrencyState] = useState(getGlobalCurrency());

useEffect(() => {
  const handleCurrencyChange = () => {
    setSelectedCurrencyState(getGlobalCurrency());
  };

  window.addEventListener("currencyChanged", handleCurrencyChange);

  return () => {
    window.removeEventListener("currencyChanged", handleCurrencyChange);
  };
}, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    // if (formData.machineIds.length === 0) {
    //   setErrorMessage('Please select at least one machine for this product.');
    //   return;
    // }
    // if (formData.selectedSlots.length === 0) {
    //   setErrorMessage('Please select at least one slot for this product.');
    //   return;
    // }
    // if (!formData.categoryId) {
    //   setErrorMessage('Please select a category.');
    //   return;
    // }

    setErrorMessage('');
    setSubmitLoading(true);

    const submitData = new FormData();
    submitData.append('name', formData.name.trim());
    submitData.append('description', formData.description.trim());
    submitData.append('categoryId', formData.categoryId.value);
    submitData.append('basePrice', parseFloat(formData.basePrice));
    submitData.append('currencyType', formData.currencyType);
    submitData.append('centralStock', parseInt(formData.centralStock));
    submitData.append('isDiscounted', formData.isDiscounted);

    if (formData.isDiscounted && formData.discountValue) {
      submitData.append('discountValue', parseFloat(formData.discountValue));
    }

    // Create machineSlots array in the format expected by backend
    const machineSlots = formData.selectedSlots.map(slot => ({
      machineId: slot.machineId,
      slotNumber: slot.slotNumber,
      stock: slot.quantity
    }));

    // Send machineSlots as JSON string
    submitData.append('machineSlots', JSON.stringify(machineSlots));

    // For editing, send info about removed machines/slots
    if (editingProduct) {
      submitData.append('replaceSlots', 'true');

      // Send original machine slots for comparison
      const originalMachineSlots = editingProduct.machineSlots || [];
      submitData.append('originalMachineSlots', JSON.stringify(originalMachineSlots));

      // Calculate removed machines
      const currentMachineIds = [...new Set(machineSlots.map(slot => slot.machineId))];
      const originalMachineIds = [...new Set(originalMachineSlots.map(slot => slot.machineId))];
      const removedMachineIds = originalMachineIds.filter(id => !currentMachineIds.includes(id));

      if (removedMachineIds.length > 0) {
        submitData.append('removedMachines', JSON.stringify(removedMachineIds));
      }
    }

    if (formData.productImage) {
      submitData.append('productImage', formData.productImage);
    }

    try {
      if (editingProduct) {
        const res = await fetchData({
          url: `${API.updateProduct(editingProduct._id)}`,
          method: "PUT",
          data: submitData,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.success) {
          refresh();
          resetForm();
        }
      } else {
        const res = await fetchData({
          url: API.createProduct,
          method: "POST",
          data: submitData,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.success) {
          refresh();
          resetForm();
        }
      }
    } catch (error) {
      console.error("Error saving product:", error);
    } finally {
      setSubmitLoading(false);
    }
  };
useEffect(() => {
  const fetchExchangeRate = async () => {
    try {
      const res = await fetchData({
        url: API.getExchangeRate,
        method: "GET",
        showMessage: false
      });

      console.log("Exchange API Response:", res);

      if (res?.success && res?.data?.exchangeRate) {
setExchangeRate(Number(res.data.exchangeRate));
      } else {
        setExchangeRate(1);
      }

    } catch (error) {
      console.error("Failed to fetch exchange rate", error);
      setExchangeRate(1);
    }
  };

  fetchExchangeRate();
}, []);



  const handleEdit = async (product) => {
    setEditingProduct(product);
    const selectedCategory = categoryOptions.find(option => option.value === product.categoryId?._id);

    setFormData({
      name: product.name,
      description: product.description || '',
      basePrice: product.basePrice?.toString() || '',
      currencyType: product.currencyType || 'LRD',
      centralStock: product.centralStock?.toString() || '',
      categoryId: selectedCategory || null,
      isDiscounted: product.isDiscounted || false,
      discountValue: product.discountValue?.toString() || '',
      machineIds: [],
      selectedSlots: [],
      productImage: null
    });

    setImagePreview(product.image ? imageUrl(product.image) : null);

    if (selectedCategory) {
      const machineOptions = await getCategoryMachines(selectedCategory.value);

      // Load previously selected machines and slots
      if (product.machineSlots && product.machineSlots.length > 0) {
        await loadExistingMachinesAndSlots(product, machineOptions);
      }
    }
    setShowModal(true);
  };

  const handleDelete = (product) => {
    const Swal = require('sweetalert2');
    Swal.fire({
      title: 'Delete Product?',
      html: `Are you sure you want to delete <strong>"${product.name}"</strong>?<br><small class="text-muted">This action cannot be undone.</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: '<i class="fas fa-trash me-2"></i>Yes, delete it!',
      cancelButtonText: '<i class="fas fa-times me-2"></i>Cancel',
      customClass: {
        confirmButton: 'border-0 shadow-none',
        cancelButton: 'border-0 shadow-none'
      },
      buttonsStyling: false,
      didOpen: () => {
        const confirmBtn = document.querySelector('.swal2-confirm');
        if (confirmBtn) {
          confirmBtn.style.backgroundColor = '#d33';
          confirmBtn.style.color = 'white';
          confirmBtn.style.padding = '0.625rem 1.25rem';
          confirmBtn.style.borderRadius = '0.25rem';
          confirmBtn.style.marginRight = '0.5rem';
          confirmBtn.style.border = 'none';
        }
        const cancelBtn = document.querySelector('.swal2-cancel');
        if (cancelBtn) {
          cancelBtn.style.backgroundColor = '#6c757d';
          cancelBtn.style.color = 'white';
          cancelBtn.style.padding = '0.625rem 1.25rem';
          cancelBtn.style.borderRadius = '0.25rem';
          cancelBtn.style.border = 'none';
        }
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetchData({
            url: `${API.deleteProduct}/${product._id}`,
            method: "DELETE"
          });
          if (res.success) {
            refresh();
            Swal.fire('Deleted!', 'Product has been deleted.', 'success');
          }
        } catch (error) {
          Swal.fire('Error!', 'Failed to delete product.', 'error');
        }
      }
    });
  };

  const handleView = async (product) => {
    console.log(product)
    const res = await getProductDataWithDetails(product._id)
    if (res.success) {

      setViewingProduct({ ...product, placements: res.data.placements });
    } else {

      setViewingProduct(product);
    }

    console.log(res)
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      basePrice: '',
      currencyType: 'LRD',
      centralStock: '',
      categoryId: null,
      machineIds: [],
      selectedSlots: [],
      isDiscounted: false,
      discountValue: '',
      productImage: null
    });
    setAvailableMachines([]);
    setAvailableSlots([]);
    setEditingProduct(null);
    setErrorMessage('');
    setImagePreview(null);
    setShowModal(false);
    setShowSlotModal(false);
    setCurrentMachineSlots([]);
    setSelectedMachineForSlots(null);
    setFetchedMachines(new Set());
  };
  console.log(viewingProduct)
  const getAllCategories = async () => {
    try {
      const res = await fetchData({
        url: API.getCategory,
        method: "GET",
        showMessage: false,
        params: {
          limit: 10000
        }
      });
      if (res.success) {
        const options = res.data.data.map(category => ({
          value: category._id,
          // label: `${category.name} (${category.machineIds?.length || 0} machines)`,
          label: `${category.name}`,
          name: category.name,
          machineIds: category.machineIds || []
        }));
        setCategoryOptions(options);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const getCategoryMachines = async (categoryName) => {
    try {
      const [machinesRes, categoriesRes] = await Promise.all([
        fetchData({
          url: API.getMachine, method: "GET", showMessage: false, params: {
            limit: 10000
          }
        }),
        fetchData({
          url: API.getCategory, method: "GET", showMessage: false, params: {
            limit: 10000
          }
        })
      ]);

      if (machinesRes.success && categoriesRes.success) {
        const selectedCategory = categoriesRes.data.data.find(cat => cat._id === categoryName);
        if (selectedCategory?.machineIds) {
          const categoryMachines = machinesRes.data.data.filter(machine =>
            selectedCategory.machineIds.includes(machine.machineId)
          );
          const machineOptions = categoryMachines.map(machine => ({
            value: machine.machineId,
            label: `${machine.name} (${machine.machineId})`
          }));
          setAvailableMachines(machineOptions);
          return machineOptions; // Return for use in handleEdit
        } else {
          setAvailableMachines([]);
          return [];
        }
      }
      return [];
    } catch (error) {
      console.error("Error fetching category machines:", error);
      return [];
    }
  };

  const handleCategoryChange = (selectedCategory) => {
    setFormData({ ...formData, categoryId: selectedCategory, machineIds: [], selectedSlots: [] });
    if (selectedCategory) {
      getCategoryMachines(selectedCategory.value);
    } else {
      setAvailableMachines([]);
      setAvailableSlots([]);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 10MB limit
        setErrorMessage('Image size should be less than 2MB');
        return;
      }
      setFormData({ ...formData, productImage: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, productImage: null });
    setImagePreview(null);
    if (editingProduct) {
      setEditingProduct({ ...editingProduct, image: null });
    }
  };

  useEffect(() => {
    getAllCategories();
  }, []);

  const filterFields = [
    {
      key: 'name',
      label: 'Product Name',
      type: 'text',
      placeholder: 'Search by name...'
    },
    {
      key: 'category',
      label: 'Category',
      type: 'text'
    },
    {
      key: 'price',
      label: 'Price',
      type: 'text'
    }
  ];

  // Fetch slots when machines are selected (with caching)
  const fetchSlotsForMachines = async (selectedMachines) => {
    if (selectedMachines.length === 0) {
      setAvailableSlots([]);
      setFetchedMachines(new Set());
      return;
    }

    try {
      const allSlots = [];
      const newFetchedMachines = new Set(fetchedMachines);

      for (const machine of selectedMachines) {
        // Only fetch if not already fetched
        if (!fetchedMachines.has(machine.value)) {
          const res = await fetchData({
            url: API.getSlots(machine.value),
            method: 'GET',
            showMessage: false
          });
          if (res?.success && res.data?.slots) {
            const machineSlots = res.data.slots.map(slot => ({
              value: slot._id,
              label: `${machine.label} - Slot ${slot.slotNumber}`,
              slotNumber: slot.slotNumber,
              machineId: machine.value,
              machineName: machine.label,
              isEmpty: !slot.productId
            }));
            allSlots.push(...machineSlots);
            newFetchedMachines.add(machine.value);
          }
        }
      }

      setFetchedMachines(newFetchedMachines);
      if (allSlots.length > 0) {
        setAvailableSlots(prev => [...prev, ...allSlots]);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  };

  // Fetch slots for a specific machine (empty for create, assigned for edit)
  const fetchSlotsForMachine = async (machineId) => {
    try {
      const res = await fetchData({
        url: API.getSlots(machineId),
        method: 'GET',
        showMessage: false
      });
      if (res?.success && res.data?.slots) {
        const totalSlots = res.data.slots.length;

        // Check if this machine already has slots assigned to the current product
        const isAlreadyAssigned = editingProduct && editingProduct.machineSlots?.some(ms => ms.machineId === machineId);

        if (editingProduct && isAlreadyAssigned) {
          // For editing existing assignment: show slots assigned to this product
          const assignedSlots = editingProduct.machineSlots
            ?.filter(ms => ms.machineId === machineId)
            .map(ms => {
              const slot = res.data.slots.find(s => s.slotNumber === ms.slotNumber);
              return slot ? {
                _id: slot._id,
                slotNumber: slot.slotNumber,
                isEmpty: false,
                selected: true,
                quantity: ms.stock || 1,
                limit: slot.limit || 10
              } : null;
            })
            .filter(Boolean) || [];

          return {
            emptySlots: assignedSlots,
            totalSlots,
            hasSlots: totalSlots > 0,
            isEmpty: assignedSlots.length === 0
          };
        } else {
          // For creating OR adding new machine during edit: show empty slots
          const emptySlots = res.data.slots
            .filter(slot => {
              const hasProduct = slot.productId || slot.product || slot.assignedProduct || slot.currentProduct;
              const hasStock = slot.stock > 0 || slot.currentStock > 0;
              const isOccupied = slot.isOccupied || slot.occupied || slot.status === 'occupied';

              return !hasProduct && !hasStock && !isOccupied;
            })
            .map(slot => ({
              _id: slot._id,
              slotNumber: slot.slotNumber,
              isEmpty: true,
              selected: false,
              quantity: 0,
              limit: slot.limit || 10
            }));

          return {
            emptySlots,
            totalSlots,
            hasSlots: totalSlots > 0,
            isEmpty: emptySlots.length === 0 && totalSlots > 0
          };
        }
      }
      return { emptySlots: [], totalSlots: 0, hasSlots: false, isEmpty: false };
    } catch (error) {
      console.error('Error fetching slots for machine:', error);
      return { emptySlots: [], totalSlots: 0, hasSlots: false, isEmpty: false };
    }
  };

  // Handle machine selection change
  const handleMachineSelectionChange = async (selectedMachines) => {
    // Check if stock quantity is filled
    if (!formData.centralStock || parseInt(formData.centralStock) <= 0) {
      setStockQuantityError(true);
      setTimeout(() => setStockQuantityError(false), 3000);
      return;
    }

    setStockQuantityError(false);
    const prevMachines = formData.machineIds;

    // Find removed machines and remove their slots
    const removedMachines = prevMachines.filter(prevMachine =>
      !selectedMachines.some(machine => machine.value === prevMachine.value)
    );

    // Remove slots for deselected machines
    let updatedSlots = formData.selectedSlots;
    if (removedMachines.length > 0) {
      const removedMachineIds = removedMachines.map(m => m.value);
      updatedSlots = formData.selectedSlots.filter(slot =>
        !removedMachineIds.includes(slot.machineId)
      );
    }

    setFormData(prev => ({
      ...prev,
      machineIds: selectedMachines,
      selectedSlots: updatedSlots
    }));

    // Find newly selected machine
    const newMachine = selectedMachines.find(machine =>
      !prevMachines.some(prev => prev.value === machine.value)
    );

    // If a new machine is selected, show slot selection popup
    if (newMachine) {
      const { emptySlots, totalSlots, hasSlots, isEmpty } = await fetchSlotsForMachine(newMachine.value);

      setCurrentMachineSlots(emptySlots);
      setSelectedMachineForSlots({
        ...newMachine,
        totalSlots,
        hasSlots,
        isEmpty,
        noSlots: !hasSlots
      });
      setShowSlotModal(true);
    }
  };

  // Handle slot selection in popup (only one slot can be selected)
  const handleSlotSelection = (slotId) => {
    setCurrentMachineSlots(prev =>
      prev.map(slot => ({
        ...slot,
        selected: slot._id === slotId
      }))
    );
  };

  // Handle quantity change for selected slot
  const handleSlotQuantityChange = (slotId, quantity) => {
    const centralStock = parseInt(formData.centralStock) || 0;

    setCurrentMachineSlots(prev =>
      prev.map(slot => {
        if (slot._id === slotId) {
          const maxQuantity = slot.limit || 10;
          let requestedQuantity = parseInt(quantity) || 0;

          // If quantity is negative, set to 0
          if (requestedQuantity < 0) {
            requestedQuantity = 0;
          }

          // Enforce the slot limit first
          if (requestedQuantity > maxQuantity) {
            requestedQuantity = maxQuantity;
          }

          // Check if requested quantity exceeds central stock
          if (requestedQuantity > centralStock) {
            requestedQuantity = centralStock;
          }

          return { ...slot, quantity: requestedQuantity };
        }
        return slot;
      })
    );
  };

  // Confirm slot selection
  const confirmSlotSelection = () => {
    const selectedSlot = currentMachineSlots.find(slot => slot.selected);
    const centralStock = parseInt(formData.centralStock) || 0;

    if (selectedSlot) {
      // Calculate total quantity already assigned to other slots
      const totalAssignedQuantity = formData.selectedSlots.reduce((total, slot) => {
        return total + (slot.quantity || 1);
      }, 0);

      // Check if adding this slot's quantity would exceed central stock
      const newTotalQuantity = totalAssignedQuantity + (selectedSlot.quantity || 1);

      if (newTotalQuantity > centralStock) {
        const Swal = require('sweetalert2');
        Swal.fire({
          title: 'Insufficient Central Stock',
          html: `Cannot assign <strong>${selectedSlot.quantity || 1}</strong> units to this slot.<br>Central stock available = <strong>${centralStock}</strong><br>Already assigned = <strong>${totalAssignedQuantity}</strong><br>Remaining stock = <strong>${centralStock - totalAssignedQuantity}</strong>`,
          icon: 'error',
          confirmButtonText: 'OK',
          customClass: {
            confirmButton: 'btn btn-primary'
          },
          buttonsStyling: false
        });
        return; // Don't proceed with slot selection
      }

      // Update form data with selected slot
      const slotOption = {
        value: selectedSlot._id,
        label: `${selectedMachineForSlots.label} - Slot ${selectedSlot.slotNumber}`,
        slotNumber: selectedSlot.slotNumber,
        machineId: selectedMachineForSlots.value,
        machineName: selectedMachineForSlots.label,
        quantity: selectedSlot.quantity
      };

      setFormData(prev => ({
        ...prev,
        selectedSlots: [...prev.selectedSlots, slotOption]
      }));
    }

    setShowSlotModal(false);
    setCurrentMachineSlots([]);
    setSelectedMachineForSlots(null);
  };

  // Load existing machines and slots for editing
  const loadExistingMachinesAndSlots = async (product, machineOptions) => {
    if (!product.machineSlots || product.machineSlots.length === 0) return;

    try {
      const uniqueMachineIds = [...new Set(product.machineSlots.map(slot => slot.machineId))];
      const selectedMachines = machineOptions.filter(machine =>
        uniqueMachineIds.includes(machine.value)
      );

      // Create selected slots array from product data
      const selectedSlots = [];

      for (const machineSlot of product.machineSlots) {
        const machine = machineOptions.find(m => m.value === machineSlot.machineId);
        if (machine) {
          selectedSlots.push({
            value: `${machineSlot.machineId}-${machineSlot.slotNumber}`,
            label: `${machine.label} - Slot ${machineSlot.slotNumber}`,
            slotNumber: machineSlot.slotNumber,
            machineId: machineSlot.machineId,
            machineName: machine.label,
            quantity: machineSlot.stock || 1
          });
        }
      }

      setFormData(prev => ({
        ...prev,
        machineIds: selectedMachines,
        selectedSlots: selectedSlots
      }));

    } catch (error) {
      console.error('Error loading existing machines and slots:', error);
    }
  };

  const calculateFinalPrice = () => {
    if (!formData.basePrice || !formData.discountValue || !formData.isDiscounted) {
      return formData.basePrice || '0.00';
    }
    const base = parseFloat(formData.basePrice);
    const discount = parseFloat(formData.discountValue);
    return (base - discount).toFixed(2);
  };

  return (
    <>
      <Container fluid className="py-4">
        <Row>
          <Col md="12">
            <Card className="shadow border-0 rounded-3 overflow-hidden">
              <div style={{ background: GRADIENTS.primary }} className="text-white py-3 px-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="mb-0 fw-semibold" style={{ fontSize: 'clamp(1rem, 4vw, 1.5rem)' }}>
                      <i className="fas fa-boxes me-2"></i>
                      <span className="d-none d-sm-inline">Products Management</span>
                      <span className="d-inline d-sm-none">Products</span>
                    </h4>
                    <p className="mb-0 opacity-75 mt-1 d-none d-md-block" style={{ fontSize: '0.9rem' }}>
                      Manage your product catalog, inventory, and pricing
                    </p>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">

                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                      className="fw-medium d-flex align-items-center"
                    >
                      <i className="fas fa-filter me-1"></i>
                      <span className="d-none d-md-inline">Filters</span>
                      {Object.values(filters).filter(v => v).length > 0 && (
                        <Badge bg="danger" className="ms-2">{Object.values(filters).filter(v => v).length}</Badge>
                      )}
                    </Button>
                    <AddButton
                      onClick={() => { resetForm(); setShowModal(true); }}
                      icon="fas fa-plus-circle"
                      variant="light"
                      className="fw-medium"
                    >
                      <span className="d-none d-md-inline">Add New Product</span>
                      <span className="d-inline d-md-none">Add</span>
                    </AddButton>
                  </div>
                </div>
              </div>

              {showFilters && (
                <div className="px-4 pt-3 border-bottom">
                  <TableFilter
                    filterConfig={filterFields}
                    filters={filters}
                    onFilterChange={updateFilters}
                    onClearFilters={clearFilters}
                  />
                </div>
              )}

              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading products...</p>
                  </div>
                ) : products && products.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="display-1 text-muted mb-3 opacity-50">
                      <i className="fas fa-box-open"></i>
                    </div>
                    <h4 className="text-muted mb-3">No Products Found</h4>
                    <p className="text-muted mb-4">Get started by adding your first product</p>
                    <Button
                      variant="primary"
                      onClick={() => { resetForm(); setShowModal(true); }}
                      className="px-4 fw-medium"
                    >
                      <i className="fas fa-plus-circle me-2"></i>Add Product
                    </Button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th className="border-0 ps-4" style={{ width: '60px' }}>Image</th>
                          <th className="border-0">Product Name</th>
                          <th className="border-0">Category</th>
                          <th className="border-0 text-center">Price</th>
                          <th className="border-0 text-center">Stock</th>
                          <th className="border-0 text-center">Status</th>
                          <th className="border-0 text-end pe-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(products && Array.isArray(products)) && products.map((product) => (
                          <tr key={product._id} className="align-middle">
                            <td className="ps-4">
                              <div className="product-image-wrapper">
                                {product.image ? (
                                  <img
                                    src={imageUrl(product.image)}
                                    alt={product.name}
                                    className="product-thumbnail"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                    onLoad={(e) => {
                                      e.target.style.display = 'block';
                                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'none';
                                    }}
                                  />
                                ) : null}
                                <div className="product-placeholder" style={{ display: product.image ? 'none' : 'flex' }}>
                                  <i className="fas fa-image text-muted"></i>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <h6 className="mb-0 fw-semibold">{product.name}</h6>
                                {product.description && (
                                  <small className="text-muted d-block mt-1 text-truncate" style={{ maxWidth: '200px' }}>
                                    {product.description}
                                  </small>
                                )}
                              </div>
                            </td>
                            <td>
                              <Badge bg="light" text="dark" className="border fw-medium">
                                <i className="fas fa-tag me-1 text-primary"></i>
                                {product.categoryId?.name || 'Uncategorized'}
                              </Badge>
                            </td>
                            <td className="text-center">
  <div className="d-flex flex-column align-items-center">

    <span className={`fw-bold ${product.isDiscounted ? 'text-success' : 'text-dark'}`}>
      {selectedCurrency}{" "}
      {convertPrice(
        product.isDiscounted ? product.finalPrice : product.basePrice,
        product.currencyType || "LRD"
      ).toFixed(2)}
    </span>

    <small className="text-muted">
      Original: {(product.currencyType || "LRD")}{" "}
      {product.isDiscounted ? product.finalPrice : product.basePrice}
    </small>

  </div>
</td>
                            <td className="text-center">
                              <div className="d-flex flex-column align-items-center">
                                <span className="fw-semibold">{product.centralStock || 0}</span>
                                <ProgressBar
                                  now={Math.min((product.centralStock || 0) * 10, 100)}
                                  variant={(product.centralStock || 0) > 10 ? 'success' : (product.centralStock || 0) > 5 ? 'warning' : 'danger'}
                                  className="w-75 mt-1"
                                  style={{ height: '4px' }}
                                />
                              </div>
                            </td>
                            <td className="text-center">
                              <Badge
                                bg={(product.centralStock || 0) > 10 ? 'success' : (product.centralStock || 0) > 5 ? 'warning' : 'danger'}
                                className="rounded-pill px-3 py-1"
                                style={{ fontSize: '0.85rem' }}
                              >
                                <i className="fas fa-circle me-1" style={{ fontSize: '0.5rem' }}></i>
                                {(product.centralStock || 0) > 10 ? 'In Stock' : (product.centralStock || 0) > 1 ? 'Low Stock' : 'Out of Stock'}
                              </Badge>
                            </td>
                            <td className="text-end pe-4 listing-actions-cell">
                              <ActionButtons
                                onEdit={() => handleEdit(product)}
                                onDelete={() => handleDelete(product)}
                                onView={() => handleView(product)}
                                showView={true}
                                size="sm"
                                variant="outline"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>

              {products && products.length > 0 && (
                <Card.Footer className="bg-light border-0 py-3 px-4">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={goToPage}
                    onNextPage={nextPage}
                    onPrevPage={prevPage}
                    onPageSizeChange={changePageSize}
                  />
                </Card.Footer>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add/Edit Product Modal */}
      <CommonModal
        visible={showModal}
        onClose={resetForm}
        title={
          <div className="d-flex align-items-center text-white">
            <div className="modal-icon bg-white bg-opacity-20 rounded-circle p-2 me-3">
              <i className={`fas ${editingProduct ? 'fa-edit' : 'fa-plus-circle'} fa-lg`}></i>
            </div>
            <div>
              <h5 className="mb-0 fw-semibold">{editingProduct ? 'Edit Product' : 'Add New Product'}</h5>
              <small className="opacity-75">
                {editingProduct ? 'Update existing product details' : 'Create a new product in your catalog'}
              </small>
            </div>
          </div>
        }
        size="xl"
        showFooter={false}
        customHeaderClass="modal-header-gradient"
      >
        <Form onSubmit={handleSubmit}>
          <div className="p-3">
            {errorMessage && (
              <Alert variant="danger" className="rounded-3 border-0 shadow-sm mb-3">
                <div className="d-flex align-items-center">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <div>
                    <h6 className="mb-1 fw-semibold">Validation Error</h6>
                    <p className="mb-0">{errorMessage}</p>
                  </div>
                </div>
              </Alert>
            )}

            <Row className="g-3">
              {/* Left Column - Basic Information */}
              <Col lg={8}>
                <div className="card border-0 bg-light rounded-3 p-3 mb-3">
                  <h6 className="fw-semibold text-dark mb-3">
                    <i className="fas fa-info-circle text-primary me-2"></i>
                    Basic Information
                  </h6>

                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium mb-2">
                          <i className="fas fa-tag text-primary me-2"></i>
                          Product Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter product name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="form-control-custom"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium mb-2">
                          <i className="fas fa-tags text-primary me-2"></i>
                          Category <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          options={categoryOptions}
                          value={formData.categoryId}
                          onChange={handleCategoryChange}
                          placeholder="Select a category..."
                          className="react-select-custom"
                          classNamePrefix="react-select"
                          isClearable
                          required
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            menu: (base) => ({ ...base, zIndex: 9999 }),
                            control: (base) => ({ ...base, minHeight: '42px' }),
                            option: (base, state) => ({
                              ...base,
                              padding: '10px 12px',
                              fontSize: '14px',
                              backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
                              color: state.isSelected ? 'white' : '#333',
                              cursor: 'pointer'
                            })
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mt-3">
                    <Form.Label className="fw-medium mb-2">
                      <i className="fas fa-align-left text-primary me-2"></i>
                      Description
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Describe your product..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="form-control-custom"
                    />
                  </Form.Group>
                </div>

                {/* Pricing Section */}
                <div className="card border-0 bg-light rounded-3 p-3 mb-3">
                  <h6 className="fw-semibold text-dark mb-3">
                    <i className="fas fa-money-bill-wave text-primary me-2"></i>
                    Pricing & Inventory
                  </h6>

                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium mb-2">
                          Base Price <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={formData.basePrice}
                            onChange={(e) => setFormData({ ...formData, basePrice: e.target.value })}
                            required
                            className="form-control-custom border-start-0"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-medium mb-2">
                          Currency Type <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={formData.currencyType}
                          onChange={(e) => setFormData({ ...formData, currencyType: e.target.value })}
                          className="form-control-custom"
                          required
                        >
                          <option value="LRD">LRD</option>
                          <option value="USD">USD</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-medium mb-2">
                          <i className="fas fa-boxes text-primary me-2"></i>
                          Stock Quantity <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            min="0"
                            placeholder="Enter quantity"
                            value={formData.centralStock}
                            onChange={(e) => {
                              setFormData({ ...formData, centralStock: e.target.value });
                              setStockQuantityError(false);
                            }}
                            required
                            className={`form-control-custom border-start-0 ${stockQuantityError ? 'border-danger' : ''}`}
                          />
                          {stockQuantityError && (
                            <Form.Text className="text-danger">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              Stock quantity is required before selecting machines
                            </Form.Text>
                          )}
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Discount Section */}
                  {/* <div className="mt-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <Form.Label className="fw-medium mb-0">
                        <i className="fas fa-percentage text-primary me-2"></i>
                        Discount Settings
                      </Form.Label>
                      <Form.Check
                        type="switch"
                        id="discount-switch"
                        label="Enable Discount"
                        checked={formData.isDiscounted}
                        onChange={(e) => setFormData({ ...formData, isDiscounted: e.target.checked, discountValue: '' })}
                        className="fw-medium"
                      />
                    </div>

                    {formData.isDiscounted && (
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-medium mb-2">Discount Amount</Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="number"
                                min="0"
                                max={formData.basePrice}
                                step="0.01"
                                placeholder="0.00"
                                value={formData.discountValue}
                                onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                                required={formData.isDiscounted}
                                className="form-control-custom border-start-0"
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-medium mb-2">Final Price</Form.Label>
                            <div className="d-flex align-items-center h-100">
                              <div className="bg-white border rounded-3 p-3 w-100">
                                <div className="text-success fw-bold fs-4">LRD{calculateFinalPrice()}</div>
                                {formData.discountValue && (
                                  <small className="text-muted">
                                    Save: LRD{formData.discountValue}
                                  </small>
                                )}
                              </div>
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                    )}
                  </div> */}
                </div>

                {/* Machines Selection */}
                {/* {availableMachines.length > 0 && (
                  <div className="card border-0 bg-light rounded-3 p-3 mb-3">
                    <h6 className="fw-semibold text-dark mb-3">
                      <i className="fas fa-desktop text-primary me-2"></i>
                      Assign to Machines <span className="text-danger">*</span>
                    </h6>
                    
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <Badge bg="primary" className="rounded-pill px-3 py-2 me-2">
                          {formData.machineIds.length} Selected
                        </Badge>
                        <small className="text-muted">
                          Select machines where this product will be available
                        </small>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleMachineSelectionChange(availableMachines)}
                          className="rounded-pill"
                        >
                          <i className="fas fa-check-double me-1"></i>Select All
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleMachineSelectionChange([])}
                          className="rounded-pill"
                          disabled={formData.machineIds.length === 0}
                        >
                          <i className="fas fa-times me-1"></i>Clear All
                        </Button>
                      </div>
                    </div>

                    <div className="row g-2">
                      {availableMachines.map((machine) => {
                        const isSelected = formData.machineIds.some(m => m.value === machine.value);
                        const hasSlots = formData.selectedSlots.some(slot => slot.machineId === machine.value);
                        return (
                          <div className="col-md-6 col-lg-4" key={`${machine.value}-${isSelected}`}>
                            <div
                              className={`machine-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                let newMachines;
                                if (isSelected) {
                                  newMachines = formData.machineIds.filter(m => m.value !== machine.value);
                                } else {
                                  newMachines = [...formData.machineIds, machine];
                                }
                                handleMachineSelectionChange(newMachines);
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                  <div className={`machine-check me-3 ${isSelected ? 'checked' : ''}`}>
                                    {isSelected && <i className="fas fa-check"></i>}
                                  </div>
                                  <div>
                                    <div className="fw-medium">{machine.label.split('(')[0].trim()}</div>
                                    <small className="text-muted">{machine.value}</small>
                                  </div>
                                </div>
                                {hasSlots && (
                                  <Badge bg="success" size="sm" className="rounded-pill">
                                    <i className="fas fa-th-large me-1"></i>
                                    {formData.selectedSlots.filter(slot => slot.machineId === machine.value).length}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )} */}

                {/* Selected Slots Display */}
                {formData.selectedSlots.length > 0 && (
                  <div className="card border-0 bg-light rounded-3 p-3">
                    <h6 className="fw-semibold text-dark mb-3">
                      <i className="fas fa-th-large text-primary me-2"></i>
                      Selected Slots <Badge bg="success" className="ms-2">{formData.selectedSlots.length}</Badge>
                    </h6>

                    <div className="row g-2">
                      {formData.selectedSlots.map((slot, index) => (
                        <div className="col-md-6 col-lg-4" key={slot.value}>
                          <div className="selected-slot-item">
                            <div className="d-flex align-items-center justify-content-between p-2 bg-white rounded-3 border">
                              <div className="d-flex align-items-center">
                                <div className="slot-number-badge me-2">
                                  {slot.slotNumber}
                                </div>
                                <div>
                                  <div className="fw-medium text-dark small">Slot {slot.slotNumber}</div>
                                  <small className="text-muted">{slot.machineName}</small>
                                  <div className="d-flex align-items-center gap-1 mt-1">
                                    <Form.Control
                                      type="number"
                                      min="1"
                                      max={formData.centralStock}
                                      value={slot.quantity || 1}
                                      onChange={(e) => {
                                        const newQuantity = parseInt(e.target.value) || 1;
                                        const centralStock = parseInt(formData.centralStock) || 0;

                                        // Calculate total quantity from other slots
                                        const otherSlotsQuantity = formData.selectedSlots
                                          .filter(s => s.value !== slot.value)
                                          .reduce((total, s) => total + (s.quantity || 1), 0);

                                        // Check if new quantity would exceed central stock
                                        if (otherSlotsQuantity + newQuantity > centralStock) {
                                          const Swal = require('sweetalert2');
                                          Swal.fire({
                                            title: 'Insufficient Central Stock',
                                            html: `Cannot assign <strong>${newQuantity}</strong> units to this slot.<br>Central stock available = <strong>${centralStock}</strong><br>Other slots using = <strong>${otherSlotsQuantity}</strong><br>Available for this slot = <strong>${centralStock - otherSlotsQuantity}</strong>`,
                                            icon: 'error',
                                            confirmButtonText: 'OK',
                                            customClass: {
                                              confirmButton: 'btn btn-primary'
                                            },
                                            buttonsStyling: false
                                          });
                                          return;
                                        }

                                        // Update the slot quantity
                                        setFormData(prev => ({
                                          ...prev,
                                          selectedSlots: prev.selectedSlots.map(s =>
                                            s.value === slot.value
                                              ? { ...s, quantity: newQuantity }
                                              : s
                                          )
                                        }));
                                      }}
                                      size="sm"
                                      className="text-center"
                                      style={{ width: '50px' }}
                                    />
                                    <small className="text-muted">qty</small>
                                  </div>
                                </div>
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    selectedSlots: prev.selectedSlots.filter(s => s.value !== slot.value)
                                  }));
                                }}
                                className="rounded-circle"
                                style={{ width: '28px', height: '28px', padding: '0' }}
                              >
                                <i className="fas fa-times"></i>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Col>

              {/* Right Column - Image Upload */}
              <Col lg={4}>
                <div className="card border-0 bg-light rounded-3 p-3 h-100">
                  <h6 className="fw-semibold text-dark mb-3">
                    <i className="fas fa-image text-primary me-2"></i>
                    Product Image
                  </h6>

                  <div className="image-upload-area text-center p-3 mb-2">
                    {imagePreview ? (
                      <div className="position-relative">
                        <img
                          src={imagePreview}
                          alt="Product Preview"
                          className="img-fluid rounded-3"
                          style={{ maxHeight: '250px', objectFit: 'contain' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 m-2"
                          onClick={removeImage}
                          style={{ borderRadius: '50%', width: '32px', height: '32px' }}
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                      </div>
                    ) : (
                      <div className="py-4">
                        <div className="mb-2">
                          <i className="fas fa-cloud-upload-alt fa-2x text-muted"></i>
                        </div>
                        <h6 className="fw-medium mb-2">Upload Product Image</h6>
                        <p className="text-muted small mb-2">
                          JPG, PNG or JPEG • Max 2MB
                        </p>
                        <Button
                          variant="outline-primary"
                          onClick={() => document.getElementById('imageInput').click()}
                          className="rounded-pill"
                        >
                          <i className="fas fa-upload me-2"></i>Choose Image
                        </Button>
                      </div>
                    )}
                  </div>

                  <Form.Control
                    id="imageInput"
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={handleImageChange}
                    className="d-none"
                  />

                  <div className="mt-2">
                    <h6 className="fw-medium mb-2">Image Guidelines</h6>
                    <ul className="list-unstyled text-muted small">
                      <li className="mb-1">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        Use high-quality images
                      </li>
                      <li className="mb-1">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        Square or 4:3 ratio works best
                      </li>
                      <li className="mb-1">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        File size under 2MB
                      </li>
                      <li>
                        <i className="fas fa-check-circle text-success me-2"></i>
                        Transparent background preferred
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Modal Footer */}
            <div className="modal-footer-custom mt-3 pt-2">
              <div className="d-flex gap-3 justify-content-end">
                <Button variant="outline-secondary" onClick={resetForm} className="btn-action" disabled={submitLoading}>
                  <i className="fas fa-times me-2"></i>Cancel
                </Button>
                <Button variant="primary" type="submit" className="btn-action" disabled={submitLoading}>
                  {submitLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      {editingProduct ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <i className={`fas ${editingProduct ? 'fa-save' : 'fa-plus-circle'} me-2`}></i>
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Form>
      </CommonModal>

      {/* View Product Details Modal */}
      <CommonModal
        visible={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={
          <div className="d-flex align-items-center text-white">
            <div className="modal-icon bg-white bg-opacity-20 rounded-circle p-2 me-3">
              <i className="fas fa-eye fa-lg"></i>
            </div>
            <div>
              <h5 className="mb-0 fw-semibold">Product Details</h5>
              <small className="opacity-75">Complete information about this product</small>
            </div>
          </div>
        }
        size="lg"
        showConfirm={false}
        cancelText="Close"
        customHeaderClass="modal-header-gradient"
      >
        {viewingProduct && (
          <div className="p-4">
            {/* Header Section */}
            <div className="text-center mb-3">
              <div className="product-image-large mb-2">
                {viewingProduct.image ? (
                  <img
                    src={imageUrl(viewingProduct.image)}
                    alt={viewingProduct.name}
                    className="img-fluid rounded-3"
                    style={{ maxHeight: '150px', objectFit: 'contain', display: 'block' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                    onLoad={(e) => {
                      e.target.style.display = 'block';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'none';
                    }}
                  />
                ) : null}
                <div className="product-image-placeholder-large" style={{ display: viewingProduct.image ? 'none' : 'flex' }}>
                  <i className="fas fa-image fa-3x text-muted"></i>
                </div>
              </div>
              <h3 className="fw-bold mb-2 text-dark">{viewingProduct.name}</h3>
              <div className="d-flex flex-wrap justify-content-center align-items-center gap-2 mb-2">
                {/* <Badge bg="primary" className="rounded-pill px-2 py-1">
                  <i className="fas fa-hashtag me-1"></i>
                  ID: {viewingProduct._id?.substring(0, 8)}...
                </Badge> */}
                <Badge bg="info" className="rounded-pill px-2 py-1">
                  <i className="fas fa-tag me-1"></i>
                  {viewingProduct.categoryId?.name || 'No Category'}
                </Badge>
              </div>
            </div>

            <hr className="border-secondary opacity-25 mb-3" />

            {/* Product Details Grid */}
            <Row className="g-2 mb-3">
              <Col md={6}>
                <div className="detail-card border-0 bg-light rounded-3 p-2">
                  <div className="d-flex align-items-center">
                    <div className="detail-icon bg-primary bg-opacity-10 text-primary rounded-circle p-2 me-2">
                      <i className="fas fa-dollar-sign"></i>
                    </div>
                    <div>
                      <small className="text-muted d-block">BASE PRICE</small>
                      <h6 className="mb-0 fw-bold text-dark">
                      {selectedCurrency}{" "}
{convertPrice(
  viewingProduct.basePrice,
  viewingProduct.currencyType || "LRD"
).toFixed(2)}
                      </h6>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="detail-card border-0 bg-light rounded-3 p-2">
                  <div className="d-flex align-items-center">
                    <div className="detail-icon bg-info bg-opacity-10 text-info rounded-circle p-2 me-2">
                      <i className="fas fa-coins"></i>
                    </div>
                    <div>
                      <small className="text-muted d-block">CURRENCY TYPE</small>
                      <h6 className="mb-0 fw-bold text-dark">{viewingProduct.currencyType || 'LRD'}</h6>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="detail-card border-0 bg-light rounded-3 p-2">
                  <div className="d-flex align-items-center">
                    <div className="detail-icon bg-success bg-opacity-10 text-success rounded-circle p-2 me-2">
                      <i className="fas fa-boxes"></i>
                    </div>
                    <div>
                      <small className="text-muted d-block">STOCK QUANTITY</small>
                      <h6 className="mb-0 fw-bold text-dark">{viewingProduct.centralStock || 0} units</h6>
                    </div>
                  </div>
                </div>
              </Col>
              {/* <Col md={6}>
                <div className="detail-card border-0 bg-light rounded-3 p-2">
                  <div className="d-flex align-items-center">
                    <div className="detail-icon bg-warning bg-opacity-10 text-warning rounded-circle p-2 me-2">
                      <i className="fas fa-percentage"></i>
                    </div>
                    <div>
                      <small className="text-muted d-block">DISCOUNT</small>
                      <h6 className="mb-0 fw-bold text-dark">
                        {viewingProduct.isDiscounted ? `LRD${viewingProduct.discountValue}` : 'No Discount'}
                      </h6>
                    </div>
                  </div>
                </div>
              </Col> */}
              <Col md={6}>
                <div className="detail-card border-0 bg-light rounded-3 p-2">
                  <div className="d-flex align-items-center">
                    <div className="detail-icon bg-info bg-opacity-10 text-info rounded-circle p-2 me-2">
                      <i className="fas fa-money-bill-wave"></i>
                    </div>
                    <div>
                      <small className="text-muted d-block">FINAL PRICE</small>
                      <h6 className="mb-0 fw-bold text-success">
                       {selectedCurrency}{" "}
{convertPrice(
  viewingProduct.isDiscounted ? viewingProduct.finalPrice : viewingProduct.basePrice,
  viewingProduct.currencyType || "LRD"
).toFixed(2)}
                      </h6>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Description */}
            {viewingProduct.description && (
              <div className="card border-0 bg-light rounded-3 p-3 mb-3">
                <h6 className="fw-semibold text-dark mb-2">
                  <i className="fas fa-align-left text-primary me-2"></i>
                  Description
                </h6>
                <p className="mb-0 text-dark">{viewingProduct.description}</p>
              </div>
            )}

            {/* Machine Slots */}
            <div className="card border-0 bg-light rounded-3 p-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-semibold text-dark mb-0">
                  <i className="fas fa-th-large text-primary me-2"></i>
                  Assigned ?
                </h6>
                <Badge bg="primary" className="rounded-pill px-2 py-1">
                  {viewingProduct?.placements && viewingProduct.placements.length}
                </Badge>
              </div>

              {viewingProduct?.placements && viewingProduct.placements?.length > 0 ? (
                <div className="row g-2">
                  {viewingProduct.placements.map((slot, index) => (
                    <div className="col-md-6 col-lg-4" key={slot._id || index}>
                      <div className="border rounded-3 p-2 bg-white">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <div className="d-flex align-items-center">
                            <div className="bg-primary bg-opacity-10 text-primary rounded-circle p-1 me-2" style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <i className="fas fa-square small"></i>
                            </div>
                            <div>
                              <h6 className="fw-bold mb-0 small"> {slot?.machine?.name}</h6>
                              <small className="text-muted">{slot?.machine?.machineId}</small>
                            </div>
                          </div>
                          <Badge bg="success" className="rounded-pill small">
                            <i className="fas fa-boxes me-1"></i>
                            {formatSlot(slot?.layerIndex, slot?.slotIndex + 1)}
                            {/* {slot?.currentStock} /{slot?.goodLaneCapacity} */}
                          </Badge>
                        </div>
                        <div className="mt-1">
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">Goods lane capacity:</small>
                            <small className="fw-medium">{slot?.goodLaneCapacity}</small>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">Stock:</small>
                            <small className="fw-medium text-success">{slot?.currentStock} units</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-3">
                  <div className="mb-2">
                    <i className="fas fa-th-large fa-2x text-muted opacity-50"></i>
                  </div>
                  <h6 className="text-muted mb-1">Not Assigned</h6>
                  <p className="text-muted mb-0 small">
                    This product is not assigned to any machine.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </CommonModal>

      {/* Slot Selection Modal */}
      <CommonModal
        visible={showSlotModal}
        onClose={() => {
          // If machine has issues OR no slot selected, deselect it from form
          const hasSelectedSlot = currentMachineSlots.find(slot => slot.selected);
          if (selectedMachineForSlots && (selectedMachineForSlots?.isEmpty || selectedMachineForSlots?.noSlots || !hasSelectedSlot)) {
            const updatedMachines = formData.machineIds.filter(m => m.value !== selectedMachineForSlots.value);
            setFormData(prev => ({
              ...prev,
              machineIds: updatedMachines
            }));
          }
          setShowSlotModal(false);
          setCurrentMachineSlots([]);
          setSelectedMachineForSlots(null);
        }}
        title={
          <div className="d-flex align-items-center text-white">
            <div className="modal-icon bg-white bg-opacity-20 rounded-circle p-2 me-3">
              <i className="fas fa-th-large fa-lg"></i>
            </div>
            <div>
              <h5 className="mb-0 fw-semibold">Select Empty Slots</h5>
              <small className="opacity-75">
                {selectedMachineForSlots?.label} - Choose available slots
              </small>
            </div>
          </div>
        }
        size="lg"
        showFooter={false}
        customHeaderClass="modal-header-gradient"
      >
        <div className="p-4">
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 className="fw-semibold mb-1">
                  {selectedMachineForSlots?.isEmpty ? 'Machine Slots Status' : 'Available Empty Slots'}
                </h6>
                <small className="text-muted">
                  {selectedMachineForSlots?.noSlots
                    ? 'This machine has no slot configuration'
                    : selectedMachineForSlots?.isEmpty
                      ? `Total slots: ${selectedMachineForSlots?.totalSlots || 0}`
                      : editingProduct && editingProduct.machineSlots?.some(ms => ms.machineId === selectedMachineForSlots?.value)
                        ? 'Currently assigned slots for this product'
                        : 'Select empty slots where this product will be placed'
                  }
                </small>
              </div>
              <Badge bg={selectedMachineForSlots?.noSlots ? 'secondary' : selectedMachineForSlots?.isEmpty ? 'danger' : 'primary'} className="rounded-pill px-3 py-2">
                {selectedMachineForSlots?.noSlots
                  ? 'No Slots'
                  : selectedMachineForSlots?.isEmpty
                    ? 'All Slots Full'
                    : editingProduct
                      ? `${currentMachineSlots.filter(slot => slot.selected).length} Assigned`
                      : currentMachineSlots.find(slot => slot.selected) ? '1 Selected' : 'Select One Slot'
                }
              </Badge>
            </div>

            {selectedMachineForSlots?.noSlots ? (
              <div className="text-center py-4">
                <div className="mb-2">
                  <i className="fas fa-cog fa-2x text-secondary"></i>
                </div>
                <h5 className="text-secondary mb-2">No Slots Configured</h5>
                <p className="text-muted mb-3">
                  Machine <strong>{selectedMachineForSlots?.label}</strong> doesn't have any slots configured yet.
                </p>
                <div className="alert alert-info border-0 rounded-3 py-2">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-info-circle me-2"></i>
                    <div className="text-start">
                      <h6 className="mb-1 fw-semibold">Next steps:</h6>
                      <ul className="mb-0 ps-3 small">
                        <li>Configure slots for this machine first</li>
                        <li>Contact administrator to set up slots</li>
                        <li>Select a different machine with slots</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : selectedMachineForSlots?.isEmpty ? (
              <div className="text-center py-4">
                <div className="mb-2">
                  <i className="fas fa-exclamation-circle fa-2x text-warning"></i>
                </div>
                <h5 className="text-warning mb-2">Machine Slots Full</h5>
                <p className="text-muted mb-3">
                  All {selectedMachineForSlots?.totalSlots} slots in <strong>{selectedMachineForSlots?.label}</strong> are currently occupied.
                </p>
                <div className="alert alert-warning border-0 rounded-3 py-2">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-info-circle me-2"></i>
                    <div className="text-start">
                      <h6 className="mb-1 fw-semibold">What you can do:</h6>
                      <ul className="mb-0 ps-3 small">
                        <li>Remove products from existing slots</li>
                        <li>Select a different machine</li>
                        <li>Add more slots to this machine</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : currentMachineSlots.length > 0 ? (
              <div className="row g-3">
                {currentMachineSlots.map((slot) => (
                  <div className="col-md-4 col-sm-6" key={slot._id}>
                    <div
                      className={`slot-card ${slot.selected ? 'selected' : ''}`}
                      onClick={() => handleSlotSelection(slot._id)}
                      style={{
                        border: '2px solid',
                        borderColor: slot.selected ? '#0d6efd' : '#dee2e6',
                        borderRadius: '8px',
                        padding: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        backgroundColor: slot.selected ? '#f8f9ff' : '#fff',
                        transform: slot.selected ? 'translateY(-2px)' : 'none',
                        boxShadow: slot.selected ? '0 4px 12px rgba(13, 110, 253, 0.15)' : '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div className="text-center">
                        <div className="mb-3">
                          <div
                            className="slot-icon"
                            style={{
                              width: '60px',
                              height: '60px',
                              borderRadius: '12px',
                              backgroundColor: slot.selected ? '#0d6efd' : '#f8f9fa',
                              color: slot.selected ? '#fff' : '#6c757d',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '0 auto',
                              fontSize: '24px',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {slot.selected ? (
                              <i className="fas fa-check"></i>
                            ) : (
                              <i className="fas fa-square"></i>
                            )}
                          </div>
                        </div>
                        <h6 className="fw-bold mb-1" style={{ color: slot.selected ? '#0d6efd' : '#212529' }}>
                          Slot {slot.slotNumber}
                        </h6>
                        <small className="text-muted">
                          {editingProduct && editingProduct.machineSlots?.some(ms => ms.machineId === selectedMachineForSlots?.value) ? 'Assigned Slot' : 'Empty Slot'}
                        </small>
                        {slot.selected && (
                          <div className="mt-2">
                            <div className="d-flex justify-content-center align-items-center gap-2">
                              <div>
                                <Form.Group>
                                  <Form.Label className="small fw-medium mb-1">Quantity</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    max={slot.limit || 10}
                                    value={slot.quantity || 0}
                                    onChange={(e) => handleSlotQuantityChange(slot._id, e.target.value)}
                                    size="sm"
                                    className="text-center"
                                    style={{ width: '60px' }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <Form.Text className="text-muted small">
                                    Max: {slot.limit || 10}
                                  </Form.Text>
                                </Form.Group>
                              </div>
                              <div className="text-center">
                                <div className="small fw-medium mb-1 text-muted">Stock</div>
                                <Badge bg="info" className="rounded-pill px-2 py-1 small">
                                  <i className="fas fa-boxes me-1"></i>
                                  {formData.centralStock || 0}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="d-flex gap-3 justify-content-end pt-3 border-top">
            <Button
              variant="outline-secondary"
              onClick={() => {
                // If machine has issues OR no slot selected, deselect it from form
                const hasSelectedSlot = currentMachineSlots.find(slot => slot.selected);
                if (selectedMachineForSlots && (selectedMachineForSlots?.isEmpty || selectedMachineForSlots?.noSlots || !hasSelectedSlot)) {
                  const updatedMachines = formData.machineIds.filter(m => m.value !== selectedMachineForSlots.value);
                  setFormData(prev => ({
                    ...prev,
                    machineIds: updatedMachines
                  }));
                }
                setShowSlotModal(false);
                setCurrentMachineSlots([]);
                setSelectedMachineForSlots(null);
              }}
              className="btn-action"
            >
              <i className="fas fa-times me-2"></i>
              {(selectedMachineForSlots?.isEmpty || selectedMachineForSlots?.noSlots) ? 'Close' : 'Cancel'}
            </Button>
            {!selectedMachineForSlots?.isEmpty && !selectedMachineForSlots?.noSlots && (
              <Button
                variant="primary"
                onClick={confirmSlotSelection}
                className="btn-action"
                disabled={!currentMachineSlots.find(slot => slot.selected)}
              >
                <i className="fas fa-check me-2"></i>
                Confirm Selection
              </Button>
            )}
          </div>
        </div>
      </CommonModal>
    </>
  );
}

export default Product;
